import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatoCantidad, formatoFecha, formatoFechaYHora } from '@/lib/utils/formato-precio';
import {
  RESULTADOS,
  cortoDelDisparo,
  cuantasDe,
  cuantasPropuso,
  cuantasReescribieron,
  reescritasEnFrase,
} from '../../lib/corridas';
import type { EventoDeLaLinea } from '../../types/corridas.types';

/** Lo que dice el `title` de una columna (maqueta: `stripHTML`). */
export function resumenDeLaColumna(e: EventoDeLaLinea): string {
  const base = `${formatoFechaYHora(e.cuando)} · ${cortoDelDisparo(e.disparo)}`;
  if (e.estado === 'EN_CURSO') return `${base} · en curso`;
  if (e.estado === 'FALLO' || e.estado === 'PERDIDA') return `${base} · no terminó`;
  const n = cuantasPropuso(e);
  if (n === 0) return `${base} · nada para proponer`;
  // Ronda del QA de R4 (R4-35): la misma frase que la tarjeta.
  const reescritas = cuantasReescribieron(e);
  const partes = RESULTADOS.filter((r) => cuantasDe(e.conteos, r.clave) > 0).map((r) => {
    const k = cuantasDe(e.conteos, r.clave);
    return `${formatoCantidad(k)} ${(k === 1 ? r.singular : r.plural).toLowerCase()}`;
  });
  if (reescritas > 0) partes.push(reescritasEnFrase(n, reescritas));
  return `${base} · ${formatoCantidad(n)} ${n === 1 ? 'propuesta' : 'propuestas'}: ${partes.join(', ')}`;
}

/**
 * Las últimas corridas de una estrategia, de la más vieja a la más nueva
 * (maqueta: `.strip`). La altura es cuántas propuso; los colores, qué pasó.
 */
export function TiraDeCorridas({
  eventos,
  onElegir,
}: {
  /** De la más nueva a la más vieja, como llegan. */
  eventos: EventoDeLaLinea[];
  onElegir: (e: EventoDeLaLinea) => void;
}) {
  if (eventos.length === 0) return null;
  const enOrden = [...eventos].reverse();
  const maximo = Math.max(1, ...eventos.map(cuantasPropuso));
  const corriendo = eventos.some((e) => e.estado === 'EN_CURSO');
  return (
    <div className="min-w-0" data-testid="tira-de-corridas">
      <p className="text-xs text-muted-foreground">
        {/* Ronda final de R4: si sólo hubo recálculos, no es «Última corrida»
            (la tarjeta dice «Todavía no corrió ninguna vez»). */}
        {eventos.every((e) => e.tipo === 'RECALCULO')
          ? eventos.length === 1
            ? 'Último recálculo'
            : `Últimos ${formatoCantidad(eventos.length)} recálculos`
          : eventos.length === 1
            ? 'Última corrida'
            : `Últimas ${formatoCantidad(eventos.length)} corridas`}
        {corriendo && <span className="text-primary"> · una en curso</span>}
      </p>
      <div className="mt-1 flex items-end gap-1.5">
        {enOrden.map((e) => {
          const titulo = resumenDeLaColumna(e);
          const noTermino = e.estado === 'FALLO' || e.estado === 'PERDIDA';
          return (
            <button
              key={e.id}
              type="button"
              title={titulo}
              aria-label={titulo}
              onClick={() => onElegir(e)}
              className="flex w-9 flex-col items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span
                className={cn(
                  'flex h-12 w-5 flex-col-reverse overflow-hidden rounded-sm bg-muted',
                  e.estado === 'EN_CURSO' && 'border border-dashed border-primary bg-transparent'
                )}
              >
                {noTermino ? (
                  <X aria-hidden="true" className="m-auto h-3.5 w-3.5 text-bloqueo" />
                ) : (
                  <>
                    {RESULTADOS.map((r) => {
                      const n = cuantasDe(e.conteos, r.clave);
                      return n > 0 ? (
                        <i
                          key={r.clave}
                          className={cn('block shrink-0', r.fondo)}
                          style={{ height: `${(n / maximo) * 100}%` }}
                        />
                      ) : null;
                    })}
                    {cuantasPropuso(e) > e.conteos.propuestas && (
                      <i
                        className="block shrink-0 bg-muted-foreground/30"
                        style={{
                          height: `${((cuantasPropuso(e) - e.conteos.propuestas) / maximo) * 100}%`,
                        }}
                      />
                    )}
                  </>
                )}
              </span>
              <span className="w-full truncate text-center text-xs text-muted-foreground">
                {formatoFecha(e.cuando)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
